import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useSectionAccess } from '../hooks/useSectionAccess';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import {
  FileText, Download, TrendingUp, TrendingDown, PieChart as PieChartIcon,
  BarChart3, FolderKanban, MapPin, Calendar, Filter, RefreshCw,
  FileSpreadsheet, File, ArrowUpRight, ArrowDownRight, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const AccountingReports = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [activeTab, setActiveTab] = useState('pl');
  
  // Date filters - Default to FY 2024-25 (April 2024 to March 2025) to match test data
  const [dateFrom, setDateFrom] = useState('2024-04-01');
  const [dateTo, setDateTo] = useState('2025-03-31');
  
  // Report data
  const [plReport, setPlReport] = useState(null);
  const [projectPL, setProjectPL] = useState(null);
  const [locationPL, setLocationPL] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [transactions, setTransactions] = useState([]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchAllReports();
    }
  }, [selectedCompany, dateFrom, dateTo]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/accounting/companies');
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAllReports = async () => {
    try {
      setLoading(true);
      const params = `date_from=${dateFrom}&date_to=${dateTo}`;
      
      const [plRes, projectRes, locationRes, txnRes] = await Promise.all([
        api.get(`/accounting/reports/profit-loss/${selectedCompany}?${params}`),
        api.get(`/accounting/reports/project-wise-pl/${selectedCompany}?${params}`),
        api.get(`/accounting/reports/location-wise-pl/${selectedCompany}?${params}`),
        api.get(`/accounting/transactions?company_id=${selectedCompany}&date_from=${dateFrom}&date_to=${dateTo}`)
      ]);
      
      setPlReport(plRes.data);
      setProjectPL(projectRes.data);
      setLocationPL(locationRes.data);
      setTransactions(txnRes.data);
      
      // Calculate trend data from transactions
      calculateTrendData(txnRes.data);
    } catch (error) {
      console.error(error);
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  const calculateTrendData = (txns) => {
    // Group transactions by month
    const monthlyData = {};
    
    txns.forEach(txn => {
      const month = txn.date?.substring(0, 7); // YYYY-MM
      if (!month) return;
      
      if (!monthlyData[month]) {
        monthlyData[month] = { month, revenue: 0, expenses: 0 };
      }
      
      txn.line_items?.forEach(item => {
        // This is simplified - in real app, you'd check ledger type
        if (!item.is_debit && txn.transaction_type === 'sales') {
          monthlyData[month].revenue += item.amount || 0;
        }
        if (item.is_debit && ['purchase', 'payment'].includes(txn.transaction_type)) {
          monthlyData[month].expenses += item.amount || 0;
        }
      });
    });
    
    const trend = Object.values(monthlyData)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map(d => ({
        ...d,
        monthLabel: new Date(d.month + '-01').toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
        profit: d.revenue - d.expenses
      }));
    
    setTrendData(trend);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    if (num >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
    if (num >= 100000) return `${(num / 100000).toFixed(2)} L`;
    return formatCurrency(num);
  };

  // Export to Excel
  const exportToExcel = (data, filename, sheetName = 'Report') => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `${filename}.xlsx`);
    toast.success('Excel file downloaded');
  };

  const exportPLToExcel = () => {
    if (!plReport) return;
    
    const data = [
      { Category: 'INCOME', Ledger: '', Amount: '' },
      ...Object.entries(plReport.income?.items || {}).map(([name, amount]) => ({
        Category: '', Ledger: name, Amount: amount
      })),
      { Category: 'Total Income', Ledger: '', Amount: plReport.income?.total || 0 },
      { Category: '', Ledger: '', Amount: '' },
      { Category: 'DIRECT EXPENSES', Ledger: '', Amount: '' },
      ...Object.entries(plReport.direct_expenses?.items || {}).map(([name, amount]) => ({
        Category: '', Ledger: name, Amount: amount
      })),
      { Category: 'Total Direct Expenses', Ledger: '', Amount: plReport.direct_expenses?.total || 0 },
      { Category: 'GROSS PROFIT', Ledger: '', Amount: plReport.gross_profit || 0 },
      { Category: '', Ledger: '', Amount: '' },
      { Category: 'INDIRECT EXPENSES', Ledger: '', Amount: '' },
      ...Object.entries(plReport.indirect_expenses?.items || {}).map(([name, amount]) => ({
        Category: '', Ledger: name, Amount: amount
      })),
      { Category: 'Total Indirect Expenses', Ledger: '', Amount: plReport.indirect_expenses?.total || 0 },
      { Category: '', Ledger: '', Amount: '' },
      { Category: 'NET PROFIT', Ledger: '', Amount: plReport.net_profit || 0 },
      { Category: 'Profit Margin %', Ledger: '', Amount: plReport.profit_margin || 0 },
    ];
    
    exportToExcel(data, `PL_Report_${dateFrom}_to_${dateTo}`, 'Profit & Loss');
  };

  const exportProjectPLToExcel = () => {
    if (!projectPL?.projects) return;
    
    const data = projectPL.projects.map(p => ({
      'Project Code': p.project_code,
      'Project Name': p.project_name,
      'Revenue': p.revenue,
      'Direct Cost': p.direct_cost,
      'Indirect Cost': p.indirect_cost,
      'Net Profit': p.net_profit,
      'Margin %': p.margin_percentage
    }));
    
    // Add totals row
    data.push({
      'Project Code': 'TOTAL',
      'Project Name': '',
      'Revenue': projectPL.totals?.revenue,
      'Direct Cost': projectPL.totals?.direct_cost,
      'Indirect Cost': projectPL.totals?.indirect_cost,
      'Net Profit': projectPL.totals?.net_profit,
      'Margin %': projectPL.totals?.margin_percentage
    });
    
    exportToExcel(data, `Project_PL_${dateFrom}_to_${dateTo}`, 'Project-wise P&L');
  };

  const exportLocationPLToExcel = () => {
    if (!locationPL?.locations) return;
    
    const data = locationPL.locations.map(l => ({
      'Location Code': l.location_code,
      'Location Name': l.location_name,
      'City': l.city,
      'Revenue': l.revenue,
      'Expenses': l.expenses,
      'Net Profit': l.net_profit,
      'Margin %': l.margin_percentage
    }));
    
    exportToExcel(data, `Location_PL_${dateFrom}_to_${dateTo}`, 'Location-wise P&L');
  };

  // Prepare chart data
  const incomeChartData = plReport?.income?.items 
    ? Object.entries(plReport.income.items).map(([name, value]) => ({ name, value }))
    : [];
  
  const expenseChartData = plReport?.indirect_expenses?.items
    ? Object.entries(plReport.indirect_expenses.items).map(([name, value]) => ({ name, value }))
    : [];

  const hasAccess = useSectionAccess('/accounting');
  if (hasAccess === false) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex flex-col items-center justify-center h-64">
          <AlertCircle className="h-16 w-16 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Access Denied</h2>
          <p className="text-gray-500">You don't have permission to access this page.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Accounting Reports</h1>
            <p className="text-gray-500">P&L statements, project analysis, and financial trends</p>
          </div>
          <Button variant="outline" onClick={fetchAllReports}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Company</label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select Company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">From Date</label>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">To Date</label>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
              <div className="flex items-end">
                <Badge className="bg-blue-100 text-blue-700 px-3 py-1.5">
                  <Calendar className="h-4 w-4 mr-1" />
                  {plReport?.period?.from} to {plReport?.period?.to}
                </Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Report Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-4 w-full max-w-2xl">
            <TabsTrigger value="pl" className="flex items-center gap-1">
              <FileText className="h-4 w-4" /> P&L Statement
            </TabsTrigger>
            <TabsTrigger value="project" className="flex items-center gap-1">
              <FolderKanban className="h-4 w-4" /> Project-wise
            </TabsTrigger>
            <TabsTrigger value="location" className="flex items-center gap-1">
              <MapPin className="h-4 w-4" /> Location-wise
            </TabsTrigger>
            <TabsTrigger value="trends" className="flex items-center gap-1">
              <TrendingUp className="h-4 w-4" /> Trends
            </TabsTrigger>
          </TabsList>

          {/* P&L Statement Tab */}
          <TabsContent value="pl" className="space-y-6">
            {plReport && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-emerald-600 font-medium">Total Income</p>
                          <p className="text-xl font-bold text-emerald-900">{formatNumber(plReport.income?.total)}</p>
                        </div>
                        <TrendingUp className="h-8 w-8 text-emerald-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-orange-600 font-medium">Direct Expenses</p>
                          <p className="text-xl font-bold text-orange-900">{formatNumber(plReport.direct_expenses?.total)}</p>
                        </div>
                        <ArrowDownRight className="h-8 w-8 text-orange-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-red-600 font-medium">Indirect Expenses</p>
                          <p className="text-xl font-bold text-red-900">{formatNumber(plReport.indirect_expenses?.total)}</p>
                        </div>
                        <TrendingDown className="h-8 w-8 text-red-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className={`bg-gradient-to-br ${plReport.net_profit >= 0 ? 'from-blue-50 to-blue-100 border-blue-200' : 'from-red-50 to-red-100 border-red-200'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className={`text-xs font-medium ${plReport.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>Net Profit</p>
                          <p className={`text-xl font-bold ${plReport.net_profit >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                            {formatNumber(plReport.net_profit)}
                          </p>
                          <p className="text-xs text-gray-500">{plReport.profit_margin}% margin</p>
                        </div>
                        {plReport.net_profit >= 0 ? 
                          <ArrowUpRight className="h-8 w-8 text-blue-500" /> : 
                          <ArrowDownRight className="h-8 w-8 text-red-500" />
                        }
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Income Chart */}
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between">
                      <CardTitle className="text-emerald-700 flex items-center gap-2">
                        <PieChartIcon className="h-5 w-5" /> Income Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {incomeChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <PieChart>
                            <Pie
                              data={incomeChartData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              label={({ name, percent }) => `${name.substring(0, 15)}... (${(percent * 100).toFixed(0)}%)`}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                            >
                              {incomeChartData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                          </PieChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-gray-500 py-12">No income data</p>
                      )}
                    </CardContent>
                  </Card>

                  {/* Expense Chart */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-red-700 flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" /> Expense Breakdown
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {expenseChartData.length > 0 ? (
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={expenseChartData} layout="vertical">
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                            <YAxis dataKey="name" type="category" width={120} tick={{ fontSize: 11 }} />
                            <Tooltip formatter={(value) => formatCurrency(value)} />
                            <Bar dataKey="value" fill="#ef4444" radius={[0, 4, 4, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      ) : (
                        <p className="text-center text-gray-500 py-12">No expense data</p>
                      )}
                    </CardContent>
                  </Card>
                </div>

                {/* P&L Statement Table */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Profit & Loss Statement</CardTitle>
                    <Button variant="outline" size="sm" onClick={exportPLToExcel}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Excel
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="text-left p-3 font-medium text-gray-600">Particulars</th>
                            <th className="text-right p-3 font-medium text-gray-600">Amount (₹)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {/* Income Section */}
                          <tr className="bg-emerald-50">
                            <td className="p-3 font-bold text-emerald-800" colSpan={2}>INCOME</td>
                          </tr>
                          {Object.entries(plReport.income?.items || {}).map(([name, amount]) => (
                            <tr key={name} className="border-b">
                              <td className="p-3 pl-6">{name}</td>
                              <td className="p-3 text-right">{formatCurrency(amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-emerald-100 font-semibold">
                            <td className="p-3">Total Income</td>
                            <td className="p-3 text-right">{formatCurrency(plReport.income?.total)}</td>
                          </tr>

                          {/* Direct Expenses */}
                          <tr className="bg-orange-50">
                            <td className="p-3 font-bold text-orange-800" colSpan={2}>DIRECT EXPENSES</td>
                          </tr>
                          {Object.entries(plReport.direct_expenses?.items || {}).map(([name, amount]) => (
                            <tr key={name} className="border-b">
                              <td className="p-3 pl-6">{name}</td>
                              <td className="p-3 text-right">{formatCurrency(amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-orange-100 font-semibold">
                            <td className="p-3">Total Direct Expenses</td>
                            <td className="p-3 text-right">{formatCurrency(plReport.direct_expenses?.total)}</td>
                          </tr>

                          {/* Gross Profit */}
                          <tr className="bg-blue-100 font-bold text-blue-900">
                            <td className="p-3">GROSS PROFIT</td>
                            <td className="p-3 text-right">{formatCurrency(plReport.gross_profit)}</td>
                          </tr>

                          {/* Indirect Expenses */}
                          <tr className="bg-red-50">
                            <td className="p-3 font-bold text-red-800" colSpan={2}>INDIRECT EXPENSES</td>
                          </tr>
                          {Object.entries(plReport.indirect_expenses?.items || {}).map(([name, amount]) => (
                            <tr key={name} className="border-b">
                              <td className="p-3 pl-6">{name}</td>
                              <td className="p-3 text-right">{formatCurrency(amount)}</td>
                            </tr>
                          ))}
                          <tr className="bg-red-100 font-semibold">
                            <td className="p-3">Total Indirect Expenses</td>
                            <td className="p-3 text-right">{formatCurrency(plReport.indirect_expenses?.total)}</td>
                          </tr>

                          {/* Net Profit */}
                          <tr className={`font-bold text-lg ${plReport.net_profit >= 0 ? 'bg-green-200 text-green-900' : 'bg-red-200 text-red-900'}`}>
                            <td className="p-4">NET PROFIT / (LOSS)</td>
                            <td className="p-4 text-right">{formatCurrency(plReport.net_profit)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Project-wise P&L Tab */}
          <TabsContent value="project" className="space-y-6">
            {projectPL && (
              <>
                {/* Totals Summary */}
                <div className="grid grid-cols-5 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-gray-500">Projects</p>
                      <p className="text-2xl font-bold text-gray-900">{projectPL.projects?.length || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-emerald-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-emerald-600">Total Revenue</p>
                      <p className="text-xl font-bold text-emerald-900">{formatNumber(projectPL.totals?.revenue)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-orange-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-orange-600">Direct Cost</p>
                      <p className="text-xl font-bold text-orange-900">{formatNumber(projectPL.totals?.direct_cost)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-red-600">Indirect Cost</p>
                      <p className="text-xl font-bold text-red-900">{formatNumber(projectPL.totals?.indirect_cost)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs text-blue-600">Net Profit</p>
                      <p className="text-xl font-bold text-blue-900">{formatNumber(projectPL.totals?.net_profit)}</p>
                      <p className="text-xs text-gray-500">{projectPL.totals?.margin_percentage}% margin</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Project Chart */}
                {projectPL.projects?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Project Revenue vs Cost Comparison</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={projectPL.projects}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="project_code" />
                          <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="revenue" name="Revenue" fill="#10b981" />
                          <Bar dataKey="direct_cost" name="Direct Cost" fill="#f59e0b" />
                          <Bar dataKey="indirect_cost" name="Indirect Cost" fill="#ef4444" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Project Table */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Project-wise Profitability</CardTitle>
                    <Button variant="outline" size="sm" onClick={exportProjectPLToExcel}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Excel
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {projectPL.projects?.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No project data. Assign transactions to projects to see this report.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-3 font-medium text-gray-600">Project</th>
                              <th className="text-right p-3 font-medium text-gray-600">Revenue</th>
                              <th className="text-right p-3 font-medium text-gray-600">Direct Cost</th>
                              <th className="text-right p-3 font-medium text-gray-600">Indirect Cost</th>
                              <th className="text-right p-3 font-medium text-gray-600">Net Profit</th>
                              <th className="text-center p-3 font-medium text-gray-600">Margin %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {projectPL.projects?.map(project => (
                              <tr key={project.project_id} className="border-b hover:bg-gray-50">
                                <td className="p-3">
                                  <div className="font-medium">{project.project_name}</div>
                                  <div className="text-xs text-gray-500">{project.project_code}</div>
                                </td>
                                <td className="p-3 text-right text-emerald-600 font-medium">{formatCurrency(project.revenue)}</td>
                                <td className="p-3 text-right text-orange-600">{formatCurrency(project.direct_cost)}</td>
                                <td className="p-3 text-right text-red-600">{formatCurrency(project.indirect_cost)}</td>
                                <td className={`p-3 text-right font-medium ${project.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                  {formatCurrency(project.net_profit)}
                                </td>
                                <td className="p-3 text-center">
                                  <Badge className={project.margin_percentage >= 20 ? 'bg-green-100 text-green-700' : project.margin_percentage >= 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                    {project.margin_percentage}%
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                            {/* Unallocated */}
                            {(projectPL.unallocated?.revenue > 0 || projectPL.unallocated?.direct_cost > 0) && (
                              <tr className="border-b bg-gray-50">
                                <td className="p-3 italic text-gray-600">Unallocated</td>
                                <td className="p-3 text-right">{formatCurrency(projectPL.unallocated?.revenue)}</td>
                                <td className="p-3 text-right">{formatCurrency(projectPL.unallocated?.direct_cost)}</td>
                                <td className="p-3 text-right">{formatCurrency(projectPL.unallocated?.indirect_cost)}</td>
                                <td className="p-3 text-right">{formatCurrency(projectPL.unallocated?.net_profit)}</td>
                                <td className="p-3 text-center">-</td>
                              </tr>
                            )}
                            {/* Totals */}
                            <tr className="bg-blue-100 font-bold">
                              <td className="p-3">TOTAL</td>
                              <td className="p-3 text-right">{formatCurrency(projectPL.totals?.revenue)}</td>
                              <td className="p-3 text-right">{formatCurrency(projectPL.totals?.direct_cost)}</td>
                              <td className="p-3 text-right">{formatCurrency(projectPL.totals?.indirect_cost)}</td>
                              <td className="p-3 text-right">{formatCurrency(projectPL.totals?.net_profit)}</td>
                              <td className="p-3 text-center">{projectPL.totals?.margin_percentage}%</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Location-wise P&L Tab */}
          <TabsContent value="location" className="space-y-6">
            {locationPL && (
              <>
                {/* Location Chart */}
                {locationPL.locations?.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Location Performance</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={locationPL.locations}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="location_name" />
                          <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                          <Tooltip formatter={(value) => formatCurrency(value)} />
                          <Legend />
                          <Bar dataKey="revenue" name="Revenue" fill="#10b981" />
                          <Bar dataKey="expenses" name="Expenses" fill="#ef4444" />
                          <Bar dataKey="net_profit" name="Net Profit" fill="#3b82f6" />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                )}

                {/* Location Table */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>Location-wise Profitability</CardTitle>
                    <Button variant="outline" size="sm" onClick={exportLocationPLToExcel}>
                      <FileSpreadsheet className="h-4 w-4 mr-2" /> Export Excel
                    </Button>
                  </CardHeader>
                  <CardContent>
                    {locationPL.locations?.length === 0 ? (
                      <p className="text-center text-gray-500 py-8">No location data. Assign transactions to locations to see this report.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="text-left p-3 font-medium text-gray-600">Location</th>
                              <th className="text-left p-3 font-medium text-gray-600">City</th>
                              <th className="text-right p-3 font-medium text-gray-600">Revenue</th>
                              <th className="text-right p-3 font-medium text-gray-600">Expenses</th>
                              <th className="text-right p-3 font-medium text-gray-600">Net Profit</th>
                              <th className="text-center p-3 font-medium text-gray-600">Margin %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {locationPL.locations?.map(location => (
                              <tr key={location.location_id} className="border-b hover:bg-gray-50">
                                <td className="p-3">
                                  <div className="font-medium">{location.location_name}</div>
                                  <div className="text-xs text-gray-500">{location.location_code}</div>
                                </td>
                                <td className="p-3 text-gray-600">{location.city || '-'}</td>
                                <td className="p-3 text-right text-emerald-600 font-medium">{formatCurrency(location.revenue)}</td>
                                <td className="p-3 text-right text-red-600">{formatCurrency(location.expenses)}</td>
                                <td className={`p-3 text-right font-medium ${location.net_profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                  {formatCurrency(location.net_profit)}
                                </td>
                                <td className="p-3 text-center">
                                  <Badge className={location.margin_percentage >= 20 ? 'bg-green-100 text-green-700' : location.margin_percentage >= 0 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                                    {location.margin_percentage}%
                                  </Badge>
                                </td>
                              </tr>
                            ))}
                            {/* Unallocated */}
                            {(locationPL.unallocated?.revenue > 0 || locationPL.unallocated?.expenses > 0) && (
                              <tr className="border-b bg-gray-50">
                                <td className="p-3 italic text-gray-600">Unallocated</td>
                                <td className="p-3">-</td>
                                <td className="p-3 text-right">{formatCurrency(locationPL.unallocated?.revenue)}</td>
                                <td className="p-3 text-right">{formatCurrency(locationPL.unallocated?.expenses)}</td>
                                <td className="p-3 text-right">{formatCurrency(locationPL.unallocated?.net_profit)}</td>
                                <td className="p-3 text-center">-</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Revenue vs Expenses Trend</CardTitle>
              </CardHeader>
              <CardContent>
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={400}>
                    <AreaChart data={trendData}>
                      <defs>
                        <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#10b981" stopOpacity={0.1}/>
                        </linearGradient>
                        <linearGradient id="colorExpenses" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#ef4444" stopOpacity={0.8}/>
                          <stop offset="95%" stopColor="#ef4444" stopOpacity={0.1}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="monthLabel" />
                      <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#10b981" fillOpacity={1} fill="url(#colorRevenue)" />
                      <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" fillOpacity={1} fill="url(#colorExpenses)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 py-12">No trend data available. Add more transactions across different months.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Profit Trend</CardTitle>
              </CardHeader>
              <CardContent>
                {trendData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={trendData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="monthLabel" />
                      <YAxis tickFormatter={(v) => `₹${(v/1000).toFixed(0)}K`} />
                      <Tooltip formatter={(value) => formatCurrency(value)} />
                      <Legend />
                      <Line type="monotone" dataKey="profit" name="Net Profit" stroke="#3b82f6" strokeWidth={3} dot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-center text-gray-500 py-12">No trend data available.</p>
                )}
              </CardContent>
            </Card>

            {/* Monthly Summary Table */}
            {trendData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Monthly Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="text-left p-3 font-medium text-gray-600">Month</th>
                          <th className="text-right p-3 font-medium text-gray-600">Revenue</th>
                          <th className="text-right p-3 font-medium text-gray-600">Expenses</th>
                          <th className="text-right p-3 font-medium text-gray-600">Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trendData.map(row => (
                          <tr key={row.month} className="border-b">
                            <td className="p-3 font-medium">{row.monthLabel}</td>
                            <td className="p-3 text-right text-emerald-600">{formatCurrency(row.revenue)}</td>
                            <td className="p-3 text-right text-red-600">{formatCurrency(row.expenses)}</td>
                            <td className={`p-3 text-right font-medium ${row.profit >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                              {formatCurrency(row.profit)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Loading State */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-20 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg flex items-center gap-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span>Loading reports...</span>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AccountingReports;
